import { createMuiTheme, responsiveFontSizes } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { modalManager, tokens } from '@facet-wealth/design';

const OFF_BLACK = '#2E2E2E';

const THE_BORDER_RADIUS = 18;

const DEBUG_COLOR_PALETTE = {
  chartreuse: '#7FFF00',
  deepPink: '#FF1493',
  dodgerBlue: '#1E90FF',
  coral: '#FF7F50',
  crimson: '#DC143C',
  gold: '#FFD700',
  lightSeaGreen: '#20B2AA',
};

// Reduces Debug color palette above and applies .5 fade opacity
const DEBUG_COLORS = Object.keys(DEBUG_COLOR_PALETTE).reduce((acc, key) => {
  acc[key] = fade(DEBUG_COLOR_PALETTE[key], 0.5);
  return acc;
}, {});

export const FONT_WEIGHTS = {
  extraBold: 700,
  bold: 500,
  medium: 400,
  book: 300,
  light: 100,
};

const TEXT_COLORS = {
  gray1: '#707070',
  gray2: '#606060',
  black: OFF_BLACK,
  white: '#FFF',
};

export const STATUS_COLORS = { // these are theme colors to be used in rebrand
  completeGreen: {
    color: '#479E73',
    contrast: TEXT_COLORS.white,
  },
  todoBlue: {
    color: '#007098',
    contrast: TEXT_COLORS.white,
  },
  progressYellow: {
    color: '#007098',
    contrast: TEXT_COLORS.white,
  },
  pendingPink: {
    color: '#B7466F',
    contrast: TEXT_COLORS.white,
  },
  reviewPurple: {
    color: '#6C70A3',
    contrast: TEXT_COLORS.white,
  },
};

export const FACET_COLORS = {
  clear: {
    color: '#FFFFFF00',
    contrast: TEXT_COLORS.black,
  },
  primaryBlue: {
    color: '#007098',
    contrast: TEXT_COLORS.white,
  },
  stone50: {
    color: tokens.color_stone_50,
  },
  stone100: {
    color: tokens.color_stone_100,
  },
  stone400: {
    color: tokens.color_stone_400,
  },
  stone500: {
    color: tokens.color_stone_500,
  },
  stone600: {
    color: tokens.color_stone_600,
  },
  stone650: {
    color: '#BEB9AF', // New color from design, lies between stone600 and stone700. Can't be put in FD because FD colors are generated programmatically and won't generate this one.
  },
  stone700: {
    color: tokens.color_stone_700,
  },
  brightRed12: {
    color: tokens.color_bright_red_12p,
  },
  brightRed30: {
    color: tokens.color_bright_red_30p,
  },
  facetDesignErrorMain: {
    color: tokens.color_bright_red_100p,
  },
  facetDesignWarningMain: {
    color: tokens.color_sunset_800,
  },
  facetDesignSuccessMain: {
    color: tokens.color_mint_100p,
  },
  oceana50: {
    color: tokens.color_oceana_50,
  },
  oceana100: {
    color: tokens.color_oceana_100,
  },
  oceana200: {
    color: tokens.color_oceana_200,
  },
  oceana700: {
    color: tokens.color_oceana_700,
  },
  oceana800: {
    color: tokens.color_oceana_800,
  },
  oceana500: {
    color: tokens.color_oceana_500,
  },
  grey20: {
    color: tokens.color_grey_20,
  },
  grey500: {
    color: tokens.color_grey_60,
  },
  colorSunset400: {
    color: tokens.color_sunset_400,
  },
  colorSunset500: {
    color: tokens.color_sunset_500,
  },
  colorStone200: {
    color: tokens.color_stone_200,
  },
  berry500: {
    color: tokens.color_berry_500,
  },
  mint100p: {
    color: tokens.color_mint_100p,
  },
  mint90p: {
    color: tokens.color_mint_90p,
  },
  brightRed12p: {
    color: tokens.color_bright_red_12p,
  },
  mint30p: {
    color: tokens.color_mint_30p,
  },
  primaryBlue30p: {
    color: '#B2D4E0',
    contrast: TEXT_COLORS.white,
  },
  primaryBlueMuted: {
    color: '#307da5',
    contrast: TEXT_COLORS.white,
  },
  primaryBlueMuted2: {
    color: '#2D6989',
    contrast: TEXT_COLORS.white,
  },
  accentBlue: {
    color: '#53B4E5',
    contrast: TEXT_COLORS.white,
  },
  accentBlue2: {
    color: '#3081B3',
    contrast: TEXT_COLORS.white,
  },
  accentBlue3: {
    color: '#00B1FF',
    contrast: TEXT_COLORS.white,
  },
  accentBlue4: {
    color: '#1A5A7C',
    contrast: TEXT_COLORS.white,
  },
  accentBlue5: {
    color: '#F5FBFF',
    contrast: TEXT_COLORS.white,
  },
  accentBlue6: {
    color: '#E5F3FC',
    contrast: TEXT_COLORS.white,
  },
  accentBlue7: {
    color: '#20485D',
    contrast: TEXT_COLORS.white,
  },
  accentBlue8: {
    color: '#83C8EB',
    contrast: TEXT_COLORS.white,
  },
  accentBlue9: {
    color: '#80C4E7',
    contrast: TEXT_COLORS.white,
  },
  accentBlue10: {
    color: '#D4EBF8',
    contrast: TEXT_COLORS.white,
  },
  accentBlue11: {
    color: '#58ADE4',
    contrast: TEXT_COLORS.white,
  },
  accentBlue12: {
    color: '#B3D1E7',
    contrast: TEXT_COLORS.white,
  },
  accentBlue13: {
    color: '#2F82B5',
    contrast: TEXT_COLORS.white,
  },
  accentBlue14: {
    color: '#8AB7D7',
    contrast: TEXT_COLORS.white,
  },
  accentBlue15: {
    color: '#9bd0ee',
    contrast: TEXT_COLORS.white,
  },
  accentBlue16: {
    color: '#c7f1ff',
    contrast: TEXT_COLORS.white,
  },
  accentBlue17: {
    color: '#DAF0F9',
    contrast: TEXT_COLORS.black,
  },
  accentBlue18: {
    color: '#f3fbff',
    contrast: TEXT_COLORS.black,
  },
  accentBlue19: {
    color: '#528AA0',
    contrast: TEXT_COLORS.black,
  },
  accentBlue20: {
    color: '#DCF0FA',
    contrast: TEXT_COLORS.black,
  },
  accentBlue21: {
    color: '#EDF7FD',
    contrast: TEXT_COLORS.black,
  },
  secondarySelected: {
    color: '#F1F9FD',
    contrast: TEXT_COLORS.black,
  },
  secondaryHover: {
    color: '#F8FCFE',
    contrast: TEXT_COLORS.black,
  },
  secondary12p: {
    color: '#F1F9FD',
    contrast: TEXT_COLORS.black,
  },
  secondary30p: {
    color: '#CBE9F7',
    contrast: TEXT_COLORS.black,
  },
  actionSelected: {
    color: '#EBEBEB',
    contrast: TEXT_COLORS.black,
  },
  accentBlueMuted: {
    color: '#EDF9FF',
    contrast: TEXT_COLORS.white,
  },
  accentBlueMuted2: {
    color: '#83B7CC',
    contrast: TEXT_COLORS.white,
  },
  accentGray: {
    color: '#DDDDDD',
    contrast: TEXT_COLORS.white,
  },
  mediumGray: {
    color: '#C9C9C9',
    contrast: TEXT_COLORS.white,
  },
  mediumGray2: {
    color: '#CBCBCB',
    contrast: TEXT_COLORS.black,
  },
  mediumGray3: {
    color: '#EDEDED',
    contrast: TEXT_COLORS.black,
  },
  mediumDarkGray: {
    color: '#9F9F9F',
    contrast: TEXT_COLORS.white,
  },
  primaryGray: {
    color: '#606060',
    contrast: TEXT_COLORS.white,
  },
  primaryGray2: {
    color: '#707070',
    contrast: TEXT_COLORS.white,
  },
  primaryGray3: {
    color: '#807F7D',
    contrast: TEXT_COLORS.white,
  },
  gray50: {
    color: '#BCBCBC',
    contrast: TEXT_COLORS.white,
  },
  primaryGray4: {
    color: '#666666',
    contrast: TEXT_COLORS.white,
  },
  actionActive: {
    color: '#757575',
    contrast: TEXT_COLORS.white,
  },
  primaryText: {
    color: '#003E54',
    contrast: TEXT_COLORS.white,
  },
  secondaryText: {
    color: '#666666',
    contrast: TEXT_COLORS.white,
  },
  secondaryEnabled: {
    color: '#A9D9F2',
    contrast: TEXT_COLORS.white,
  },
  darkGray: {
    color: '#3C3C3C',
    contrast: TEXT_COLORS.white,
  },
  blueGray: {
    color: '#7794A7',
    contrast: TEXT_COLORS.white,
  },
  blueGray2: {
    color: '#649DB5',
    contrast: TEXT_COLORS.white,
  },
  blueGray3: {
    color: '#E8EEf2',
    contrast: TEXT_COLORS.white,
  },
  blueGray4: {
    color: '#2F6E94',
    contrast: TEXT_COLORS.white,
  },
  naturalGreen: {
    color: '#124A1F',
    contrast: TEXT_COLORS.white,
  },
  accentGreen: {
    color: '#38987b',
    contrast: TEXT_COLORS.white,
  },
  accentGreen2: {
    color: '#3B9B7D',
    contrast: TEXT_COLORS.white,
  },
  accentGreen3: {
    color: '#51CCCC',
    contrast: TEXT_COLORS.white,
  },
  accentGreen4: {
    color: '#64E2BB',
    contrast: TEXT_COLORS.white,
  },
  seafoam: {
    color: '#64E2BB',
    contrast: TEXT_COLORS.white,
  },
  accentGreen5: {
    color: '#45AAAA',
    contrast: TEXT_COLORS.white,
  },
  accentGreen6: {
    color: '#CAF0F0',
    contrast: TEXT_COLORS.white,
  },
  accentGreen7: {
    color: '#338E96',
    contrast: TEXT_COLORS.white,
  },
  accentGreen8: {
    color: '#EAFFFA',
    contrast: TEXT_COLORS.white,
  },
  accentGreen9: {
    color: '#D8F6DA',
    contrast: TEXT_COLORS.black,
  },
  accentGreen10: {
    color: '#D6EAE4',
    contrast: TEXT_COLORS.black,
  },
  accentGreen11: {
    color: '#EFFCF8',
    contrast: TEXT_COLORS.black,
  },
  lightGray: {
    color: '#f6f5f0',
    contrast: TEXT_COLORS.white,
  },
  lightGray2: {
    color: '#E0E0E0',
    contrast: TEXT_COLORS.white,
  },
  lightGray3: {
    color: '#E8E8E8',
    contrast: TEXT_COLORS.white,
  },
  lightGray4: {
    color: '#E3E8EE',
    contrast: TEXT_COLORS.white,
  },
  lightGray5: {
    color: '#F8F8F8',
    contrast: TEXT_COLORS.black,
  },
  backgroundGray: {
    color: '#F7F8F8',
    contrast: TEXT_COLORS.white,
  },
  backgroundGray2: {
    color: '#F4F4F4',
    contrast: TEXT_COLORS.black,
  },
  yellow: {
    color: '#F8D59B',
    contrast: TEXT_COLORS.black,
  },
  orange: {
    color: '#f7901e',
    contrast: TEXT_COLORS.white,
  },
  accentYellow1: {
    color: '#FEE9D2',
    contrast: TEXT_COLORS.black,
  },
  red: {
    color: '#842323',
    contrast: TEXT_COLORS.white,
  },
  accentRed: {
    color: '#EF7D7D',
    contrast: TEXT_COLORS.white,
  },
  accentRed2: {
    color: '#ED6262',
    contrast: TEXT_COLORS.white,
  },
  accentRed3: {
    color: '#f5d5d5',
    contrast: TEXT_COLORS.white,
  },
  accentRed4: {
    color: '#E6D3D3',
    contrast: TEXT_COLORS.black,
  },
  alertRed: {
    color: '#C13131',
    contrast: TEXT_COLORS.white,
  },
  border: {
    color: '#D8D8D8',
    contrast: TEXT_COLORS.white,
  },
  slate: {
    color: '#333333',
    contrast: TEXT_COLORS.white,
  },
  purple: {
    color: '#9364B0',
    contrast: TEXT_COLORS.white,
  },
  accentPurple: {
    color: '#5E18A3',
    contrast: TEXT_COLORS.white,
  },
  liablePurple: {
    color: '#BEA2D0',
    contrast: TEXT_COLORS.white,
  },
  pdfRed: {
    color: '#F40F02',
    contrast: TEXT_COLORS.white,
  },
  msWordBlue: {
    color: '#00A2ED',
    contrast: TEXT_COLORS.white,
  },
  msExcelGreen: {
    color: '#1D6F42',
    contrast: TEXT_COLORS.white,
  },
  msPowerpointOrange: {
    color: '#D04423',
    contrast: TEXT_COLORS.white,
  },
  whiteSmoke: {
    color: '#F8F8F8',
    contrast: TEXT_COLORS.white,
  },
  translucent: {
    color: 'rgba(255,255,255,0.1)',
    contrast: TEXT_COLORS.white,
  },
  gentlyFrosted: {
    color: 'rgba(255,255,255,0.04)',
    contrast: TEXT_COLORS.black,
  },
  frosted: {
    color: 'rgba(255,255,255,0.5)',
    contrast: TEXT_COLORS.white,
  },
  muiMediumGray: {
    color: 'rgba(0,0,0,0.23)',
    contrast: TEXT_COLORS.white,
  },
  completeGreen: {
    color: '#479E73',
    contrast: TEXT_COLORS.white,
  },
  progressYellow: {
    color: '#9C8D49',
    contrast: TEXT_COLORS.white,
  },
  pendingPink: {
    color: '#B7466F',
    contrast: TEXT_COLORS.white,
  },
  reviewPurple: {
    color: '#6C70A3',
    contrast: TEXT_COLORS.white,
  },
  todoBlue: {
    color: '#007098',
    contrast: TEXT_COLORS.white,
  },
  jet500: {
    color: tokens.color_jet_500,
    contrast: TEXT_COLORS.white,
  },
  jet200: {
    color: tokens.color_jet_200,
    contrast: TEXT_COLORS.black,
  },
  maroon30: {
    color: tokens.color_maroon_30p,
    contrast: TEXT_COLORS.black,
  },
};

// Sanity check - if someone removes a token from FD for example, we'd want the webapp to blow up in pipes
for (const [colorKey, color] of Object.entries(FACET_COLORS)) {
  for (const [propKey, propVal] of Object.entries(color)) {
    if (!propVal) throw new Error(`FACET_COLORS.${colorKey}.${propKey} is falsey!`);
  }
}

export const PALETTE = {
  common: {
    black: OFF_BLACK,
  },
  primary: {
    main: '#007098',
    dark: '#004E6A',
  },
  secondary: {
    main: '#38997B',
  },
  warning: {
    main: '#f7901e', /* If you change this, change the success color in App.css .notification-warning and vice versa. They should match */
  },
  error: {
    main: '#842323', /* If you change this, change the success color in App.css .notification-error and vice versa. They should match */
  },
  success: {
    main: '#38997B', /* If you change this, change the success color in App.css .notification-success and vice versa. They should match */
  },
  disabled: {
    main: '#ADADAD',
  },
  disabledBackground: {
    main: '#0000001F',
  },
  text: {
    primary: '#2E2E2E',
    secondary: '#666666',
  },
  subtle: {
    lightest: tokens.color_stone_200,
    main: tokens.color_stone_500,
  },
  facet: Object.keys(FACET_COLORS).reduce((acc, curr) => {
    return {
      ...acc,
      [curr]: FACET_COLORS[curr].color,
    };
  }, {}),
  debug: DEBUG_COLORS,
};

// Create CSS Theme Variables
for (const [key, value] of Object.entries(FACET_COLORS)) {
  document.documentElement.style.setProperty(`--color-${key}`, `${value.color}`);
}

const defaultTheme = createMuiTheme();

export default responsiveFontSizes(
  createMuiTheme({
    typography: {
      fontSizePt625Rem: '.625rem', // Matches <FDLabelText size="sm" />
      fontSizePt75Rem: '.75rem',
      fontSizePt875Rem: '.875rem',
      fontSize1Rem: '1rem',
      fontSize2Rem: '2rem',
      fontSize1Pt25Rem: '1.25rem',
      fontSize1Pt125Rem: '1.125rem',
      fontSize1Pt625Rem: '1.625rem',
      fontWeightLight: 100,
      fontWeightRegular: 300,
      fontWeightMedium: 400,
      fontWeightBold: 500,
      fontWeightExtraBold: 700,
      fontWeightFacetBold: FONT_WEIGHTS.bold,
      fontWeightFacetMedium: FONT_WEIGHTS.medium,
      fontWeightFacetBook: FONT_WEIGHTS.book,
      fontFamily: ['Euclid Square', 'sans-serif'].join(','),
      h1: {
        fontSize: '2rem',
        fontWeight: 600,
      },
      h2: {
        fontSize: '1.5rem',
        fontWeight: 400,
      },
      h3: {
        fontSize: '1.375rem',
        fontWeight: 400,
      },
      h4: {
        fontSize: '1.125rem',
        fontWeight: 400,
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 400,
      },
      h6: {
        fontSize: '0.875rem',
        fontWeight: 400,
      },
      body1: {
        fontSize: '0.75rem',
        fontWeight: 300,
      },
      body2: {
        fontSize: '0.625rem',
        fontWeight: 300,
      },
      // subtitle1: {},
      // subtitle2: {},
      // button: {},
      caption: {
        fontWeight: 500,
      },
      // overline: {},
    },
    palette: PALETTE,
    filters: {
      primaryBlue: 'invert(23%) sepia(12%) saturate(6294%) hue-rotate(170deg) brightness(103%) contrast(81%)',
      error: 'brightness(0%) saturate(100%) invert(9%) sepia(82%) saturate(4723%) hue-rotate(356deg) brightness(103%) contrast(96%);',
    },
    gradients: {
      horizontal: {
        primaryToAccentBlue: `transparent linear-gradient(90deg, ${PALETTE.primary.main} 0%, ${PALETTE.facet.accentBlue} 100%) 0% 0% no-repeat padding-box`,
      },
      vertical: {
        primaryToAccentBlue: `transparent linear-gradient(174deg, ${PALETTE.primary.main} 0%, ${PALETTE.facet.accentBlue} 250%) 0% 0% no-repeat padding-box`,
        primaryToMutedBlue: `linear-gradient(${PALETTE.facet.primaryBlue}, ${PALETTE.facet.primaryBlueMuted})`,
      },
      radial: {
        goalsFlareBackground: {
          desktop: 'radial-gradient(25% 10% at top,rgb(100 226 187 / 65%),rgb(83 180 229 / 30%),rgb(255 0 0 / 0%))',
          mobile: 'radial-gradient(100% 25% at top,rgb(100 226 187 / 65%),rgb(83 180 229 / 30%),rgb(255 0 0 / 0%))',
        },
      },
    },
    radius: {
      none: 0,
      xs: 6,
      sm: 10,
      md: THE_BORDER_RADIUS,
      lg: 24,
      xl: 32,
      xxl: 64,
      full: '9999px',
    },
    heights: {
      feedNewThreadInputCardDefaultHeight: 186,
      advisorNavigationHeight: 48, // Search for "advisorNavigationHeight" and adjust where necessary.
      advisorActivityFeedHeaderHeight: 123, // Search for "advisorActivityFeedHeaderHeight" and adjust where necessary.
      clientNavigationHeight: 48, // Search for "clientNavigationHeight" and adjust where necessary.
      clientActivityFeedHeaderHeight: 95,
      mobileNavigationHeight: 56,
      mobileBottomNavigationHeight: 56,
      desktopTopNavigationHeight: 64,
      standardHeaderHeight: 64,
      inboxMessageHeaderHeight: 100,
      footerHeight: 82, // Search for "footerHeight" and just where necessary when changing.
      monthlyCalendarDayHeaderHeight: 32,
      monthlyCalendarDayItemHeight: 20,
      weeklyCalendarDayHeaderHeight: 48,
      tabs: 40,
      modalHeader: 40,
      advisorActivityFeedMobileHeaderHeight: 75,
    },
    shadows: [
      // If ever adding any new shadow slots then make sure to increment this number by 1.
      ...defaultTheme.shadows.slice(0, defaultTheme.shadows.length - 3),
      '', // elevation={21} Paper variant not yet used. Just here as an example for how this can be extended.
      '0px 1px 5px #d9d9d9', // elevation={22} Paper variant "tight"
      '0px 3px 15px #0000000B', // elevation={23} Paper variant "subtle"
      '0px 3px 15px #0000001D',
    ],
    dimensions: {
      xxs: 120,
      xs: 200,
      sm: 300,
      md: 400,
      lg: 600,
      xl: 800,
      xxl: 1300,
      xxxl: 1600,
      xxxxl: 1800,
      smallPhoneWidth: 320,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1536,
      },
    },
    zIndex: {
      mobileStepper: 1000,
      speedDial: 1050,
      appBar: 1100,
      sideBar: 1150,
      drawer: 1200,
      modal: 1300,
      snackbar: 1400,
      tooltip: 1500,
    },
    props: {
      MuiDialog: {
        'data-modal-type': 'dialog',
      },
      MuiModal: {
        manager: modalManager,
      },
    },
    overrides: {
      MuiButton: {
        root: {
          textTransform: 'none',
        },
        textSizeLarge: {
          fontSize: '1.25rem',
        },
        outlinedSizeLarge: {
          fontSize: '1.25rem',
        },
        containedSizeLarge: {
          fontSize: '1.25rem',
        },
        textSizeSmall: {
          fontSize: '0.75rem',
        },
        outlinedSizeSmall: {
          fontSize: '0.75rem',
        },
        containedSizeSmall: {
          fontSize: '0.75rem',
        },
      },
      MuiFab: {
        root: {
          textTransform: 'none',
        },
      },
      MuiOutlinedInput: {
        root: {
          '&:hover $notchedOutline': {
            borderColor: 'rgba(0,0,0,0.23)',
          },
        },
      },
      MuiSelect: {
        selectMenu: {
          lineHeight: '1.1876em',
        },
      },
      MuiDialogActions: {
        root: {
          padding: 16,
        },
      },
      MuiFilledInput: {
        input: {
          '&:-webkit-autofill': {
            '-webkit-text-fill-color': PALETTE.primary.main,
            '-webkit-box-shadow': '0 0 0px 1000px #ffffff00 inset',
            transition: 'background-color 600000s 0s, color 600000s 0s',
          },
        },
      },
      MuiChip: {
        root: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
    },
    transitions: {
      duration: {
        xl: 1500,
      },
    },
    flexbox: {
      displayFlex: 'flex',
      justifyContentSpaceBetween: 'space-between',
    },
  }),
  {
    factor: 1.5,
  },
);
